import { useState } from "react";
import { Construction, Mail, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { SEOHead } from "@/components/SEO/SEOProvider";

const MaintenanceMode = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { toast } = useToast();

  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast({
        title: "Email invalide",
        description: "Veuillez entrer une adresse email valide.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.rpc("subscribe_to_newsletter", {
        email_address: email,
      });

      if (error) throw error;

      setIsSubscribed(true);
      toast({
        title: "Merci !",
        description: "Vous serez informé dès le lancement.",
      });
      setEmail("");
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Site en construction - AssuranceDO.fr"
        description="Notre site est en cours de construction. Inscrivez-vous pour être informé du lancement."
        canonical="https://assurancedo.fr"
      />

      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-secondary/5 px-4">
        <div className="max-w-2xl w-full text-center space-y-8 animate-fade-in">
          {/* Animation icône construction */}
          <div className="relative inline-block">
            <div className="absolute inset-0 bg-primary/20 rounded-full blur-3xl animate-pulse" />
            <Construction
              className="w-20 h-20 mx-auto text-primary relative animate-[bounce_3s_ease-in-out_infinite]"
              strokeWidth={1.5}
            />
          </div>

          {/* Titre principal */}
          <div className="space-y-4">
            <h1 className="text-4xl md:text-6xl font-bold text-foreground tracking-tight">
              Quelque chose de grand
              <br />
              <span className="text-primary">se construit ici</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground max-w-xl mx-auto leading-relaxed">
              AssuranceDO.fr arrive bientôt pour vous aider à comprendre l'assurance dommages-ouvrage.
            </p>
          </div>

          {/* Section Newsletter */}
          {!isSubscribed ? (
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 shadow-elegant max-w-md mx-auto">
              <div className="space-y-4">
                <div className="flex items-center justify-center gap-2 text-primary">
                  <Mail className="w-5 h-5" />
                  <h2 className="text-lg font-semibold">Restez informé</h2>
                </div>

                <p className="text-sm text-muted-foreground">
                  Soyez parmi les premiers à découvrir notre plateforme lors du lancement.
                </p>

                <form onSubmit={handleNewsletterSubmit} className="space-y-3">
                  <Input
                    type="email"
                    placeholder="votre@email.fr"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full"
                    disabled={isSubmitting}
                  />
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? "Inscription..." : "Me tenir informé"}
                  </Button>
                </form>
              </div>
            </div>
          ) : (
            <div className="bg-card/50 backdrop-blur-sm border border-border rounded-2xl p-8 shadow-elegant max-w-md mx-auto">
              <div className="flex flex-col items-center gap-4 text-primary">
                <CheckCircle className="w-12 h-12" />
                <h2 className="text-lg font-semibold">Merci !</h2>
                <p className="text-sm text-muted-foreground">Vous serez informé dès que nous serons prêts.</p>
              </div>
            </div>
          )}

          {/* Message mystérieux */}
          <p className="text-sm text-muted-foreground/70 italic">
            Notre chantier digital est en cours, mais l'histoire commence déjà ici...
          </p>
        </div>
      </div>
    </>
  );
};

export default MaintenanceMode;
